import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FiArrowLeft,
  FiCheckCircle,
  FiCompass,
  FiEye,
  FiFeather,
  FiHeart,
  FiLayers,
  FiSearch,
  FiShield,
  FiTrendingUp,
  FiUsers,
} from 'react-icons/fi';
import { useLocale } from '../context/LocaleContext';
import './About.css';

const values = [
  { icon: FiShield, key: 'quality' },
  { icon: FiHeart, key: 'honesty' },
  { icon: FiTrendingUp, key: 'growth' },
  { icon: FiUsers, key: 'collaboration' },
];

const processSteps = [
  { number: 1, icon: FiSearch, key: 'discovery' },
  { number: 2, icon: FiCompass, key: 'strategy' },
  { number: 3, icon: FiLayers, key: 'delivery' },
  { number: 4, icon: FiTrendingUp, key: 'growth' },
];

const About = () => {
  const { t } = useTranslation();
  const { localePath, formatNumber } = useLocale();
  return (
  <main className="about-v2">
    <section className="about-v2-hero" aria-labelledby="about-page-title">
      <div className="about-v2-shell about-v2-hero-grid">
        <div className="about-v2-hero-copy">
          <span className="about-v2-eyebrow"><FiFeather aria-hidden="true" /> {t('about.eyebrow')}</span>
          <h1 id="about-page-title">{t('about.title')}</h1>
          <p>{t('about.intro')}</p>
          <div className="about-v2-hero-actions">
            <Link to={localePath('/services')} className="about-v2-button about-v2-button-primary">
              {t('about.services')} <FiArrowLeft aria-hidden="true" />
            </Link>
            <Link to={localePath('/contact')} className="about-v2-button about-v2-button-secondary">{t('about.talk')}</Link>
          </div>
          <ul className="about-v2-trust-list" aria-label={t('about.commitmentsLabel')}>
            <li><FiCheckCircle aria-hidden="true" /> {t('about.custom')}</li>
            <li><FiCheckCircle aria-hidden="true" /> {t('about.transparent')}</li>
            <li><FiCheckCircle aria-hidden="true" /> {t('about.support')}</li>
          </ul>
        </div>

        <figure className="about-v2-hero-visual">
          <div className="about-v2-image-frame">
            <img src="/images/AboutUs/people-websites.webp" alt={t('about.heroImageAlt')} />
          </div>
          <figcaption className="about-v2-floating-note">
            <span>{t('about.philosophy')}</span>
            <strong>{t('about.philosophyText')}</strong>
          </figcaption>
          <span className="about-v2-visual-label" aria-hidden="true">{t('about.rootsToFuture')}</span>
        </figure>
      </div>
    </section>

    <section className="about-v2-story about-v2-section" aria-labelledby="about-story-title">
      <div className="about-v2-shell about-v2-story-grid">
        <div className="about-v2-story-image">
          <img src="/images/AboutUs/kurdish-craft.webp" alt={t('about.craftImageAlt')} />
          <div className="about-v2-name-card">
            <span>{t('home.title')}</span>
            <p>{t('about.nameMeaning')}</p>
          </div>
        </div>
        <div className="about-v2-story-copy">
          <span className="about-v2-section-kicker">{t('about.storyKicker')}</span>
          <h2 id="about-story-title">{t('about.storyTitle')}</h2>
          <p className="about-v2-story-lead">
            {t('about.storyLead')}
          </p>
          <p>
            {t('about.storyBody')}
          </p>
          <blockquote>
            {t('about.storyQuote')}
          </blockquote>
        </div>
      </div>
    </section>

    <section className="about-v2-purpose about-v2-section" aria-labelledby="about-purpose-title">
      <div className="about-v2-shell">
        <header className="about-v2-section-heading">
          <span className="about-v2-section-kicker">{t('about.purposeKicker')}</span>
          <h2 id="about-purpose-title">{t('about.purposeTitle')}</h2>
        </header>
        <div className="about-v2-purpose-grid">
          <article className="about-v2-purpose-card is-mission">
            <span className="about-v2-purpose-icon"><FiCompass aria-hidden="true" /></span>
            <div>
              <span className="about-v2-card-number">{formatNumber(1)}</span>
              <h3>{t('about.missionTitle')}</h3>
              <p>{t('about.missionText')}</p>
            </div>
          </article>
          <article className="about-v2-purpose-card is-vision">
            <span className="about-v2-purpose-icon"><FiEye aria-hidden="true" /></span>
            <div>
              <span className="about-v2-card-number">{formatNumber(2)}</span>
              <h3>{t('about.visionTitle')}</h3>
              <p>{t('about.visionText')}</p>
            </div>
          </article>
        </div>
      </div>
    </section>

    <section className="about-v2-values about-v2-section" aria-labelledby="about-values-title">
      <div className="about-v2-shell">
        <header className="about-v2-section-heading">
          <span className="about-v2-section-kicker">{t('about.valuesKicker')}</span>
          <h2 id="about-values-title">{t('about.valuesTitle')}</h2>
          <p>{t('about.valuesIntro')}</p>
        </header>
        <div className="about-v2-values-grid">
          {values.map(({ icon: Icon, key }) => (
            <article className="about-v2-value-card" key={key}>
              <span className="about-v2-value-icon"><Icon aria-hidden="true" /></span>
              <h3>{t(`about.values.${key}.title`)}</h3>
              <p>{t(`about.values.${key}.text`)}</p>
            </article>
          ))}
        </div>
      </div>
    </section>

    <section className="about-v2-process about-v2-section" aria-labelledby="about-process-title">
      <div className="about-v2-shell">
        <header className="about-v2-section-heading is-light">
          <span className="about-v2-section-kicker">{t('about.processKicker')}</span>
          <h2 id="about-process-title">{t('about.processTitle')}</h2>
          <p>{t('about.processIntro')}</p>
        </header>
        <ol className="about-v2-process-list">
          {processSteps.map(({ number, icon: Icon, key }) => (
            <li key={number}>
              <span className="about-v2-process-number">{formatNumber(number)}</span>
              <span className="about-v2-process-icon"><Icon aria-hidden="true" /></span>
              <h3>{t(`about.process.${key}.title`)}</h3>
              <p>{t(`about.process.${key}.text`)}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>

    <section className="about-v2-founder about-v2-section" aria-labelledby="about-founder-title">
      <div className="about-v2-shell about-v2-founder-card">
        <div className="about-v2-founder-image">
          <img src="/images/AboutUs/ceo/masoud-ekhtiyari.jpg" alt={t('about.founderImageAlt')} />
          <span aria-hidden="true" />
        </div>
        <div className="about-v2-founder-copy">
          <span className="about-v2-section-kicker">{t('about.founderKicker')}</span>
          <h2 id="about-founder-title">{t('about.founderName')}</h2>
          <p className="about-v2-founder-role">{t('about.founderRole')}</p>
          <p>
            {t('about.founderText')}
          </p>
        </div>
      </div>
    </section>

    <section className="about-v2-cta" aria-labelledby="about-cta-title">
      <div className="about-v2-shell about-v2-cta-card">
        <div>
          <span>{t('about.ctaHint')}</span>
          <h2 id="about-cta-title">{t('about.ctaTitle')}</h2>
        </div>
        <Link to={localePath('/contact')} className="about-v2-button about-v2-button-light">
          {t('about.defineProject')} <FiArrowLeft aria-hidden="true" />
        </Link>
      </div>
    </section>
  </main>
  );
};

export default About;
